import React, { useState, useEffect } from 'react';
import { Clock, Sun, Sunrise, Moon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent } from '../ui/card';

// Hijri Calendar Conversion
const gregorianToHijri = (date) => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  
  // Julian Day calculation
  const jd = Math.floor((1461 * (year + 4800 + Math.floor((month - 14) / 12))) / 4) +
             Math.floor((367 * (month - 2 - 12 * Math.floor((month - 14) / 12))) / 12) -
             Math.floor((3 * Math.floor((year + 4900 + Math.floor((month - 14) / 12)) / 100)) / 4) +
             day - 32075;
  
  // Hijri calculation
  const l = jd - 1948440 + 10632;
  const n = Math.floor((l - 1) / 10631);
  const l2 = l - 10631 * n + 354;
  const j = Math.floor((10985 - l2) / 5316) * Math.floor((50 * l2) / 17719) +
            Math.floor(l2 / 5670) * Math.floor((43 * l2) / 15238);
  const l3 = l2 - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50) -
             Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29;
  const hijriMonth = Math.floor((24 * l3) / 709);
  const hijriDay = l3 - Math.floor((709 * hijriMonth) / 24);
  const hijriYear = 30 * n + j - 30;
  
  return { day: hijriDay, month: hijriMonth, year: hijriYear };
};

const hijriMonthNames = {
  en: ['Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani', 'Jumada al-Awwal', 'Jumada al-Thani', 
       'Rajab', 'Shaban', 'Ramadan', 'Shawwal', 'Dhul Qadah', 'Dhul Hijjah'],
  ar: ['محرم', 'صفر', 'ربيع الأول', 'ربيع الثاني', 'جمادى الأولى', 'جمادى الآخرة',
       'رجب', 'شعبان', 'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'],
  tr: ['Muharrem', 'Safer', 'Rebiülevvel', 'Rebiülahir', 'Cemaziyelevvel', 'Cemaziyelahir',
       'Recep', 'Şaban', 'Ramazan', 'Şevval', 'Zilkade', 'Zilhicce']
};

const PrayerWidget = ({ content }) => {
  const { t, language } = useLanguage();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Get Northern Cyprus time (UTC+3 for EEST - Eastern European Summer Time)
  const getCyprusTime = () => {
    const utc = currentTime.getTime() + (currentTime.getTimezoneOffset() * 60000);
    return new Date(utc + (3 * 3600000)); // UTC+3 for Northern Cyprus summer time
  };

  const cyprusTime = getCyprusTime();

  // Format current time for Cyprus
  const formatCurrentTime = () => {
    return cyprusTime.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: true 
    });
  };

  // Format Gregorian date
  const formatGregorianDate = () => {
    return cyprusTime.toLocaleDateString(language === 'ar' ? 'ar-SA' : language === 'tr' ? 'tr-TR' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format Hijri date
  const formatHijriDate = () => {
    const hijri = gregorianToHijri(cyprusTime);
    const monthName = hijriMonthNames[language]?.[hijri.month - 1] || hijriMonthNames.en[hijri.month - 1];
    return `${hijri.day} ${monthName} ${hijri.year} AH`;
  };

  const prayers = [
    {
      name: t('prayers.fajr'),
      time: content?.fajr_time || '5:30 AM',
      icon: Moon,
      color: 'bg-indigo-500'
    },
    {
      name: t('prayers.sunrise'),
      time: content?.sunrise_time || '6:45 AM',
      icon: Sunrise,
      color: 'bg-orange-400'
    },
    {
      name: t('prayers.dhuhr'),
      time: content?.dhuhr_time || '12:30 PM',
      icon: Sun,
      color: 'bg-yellow-500'
    },
    {
      name: t('prayers.asr'),
      time: content?.asr_time || '3:45 PM',
      icon: Sun,
      color: 'bg-amber-500'
    },
    {
      name: t('prayers.maghrib'),
      time: content?.maghrib_time || '6:15 PM',
      icon: Sunrise,
      color: 'bg-orange-600'
    },
    {
      name: t('prayers.isha'),
      time: content?.isha_time || '8:30 PM',
      icon: Moon,
      color: 'bg-indigo-700'
    }
  ];

  return (
    <Card 
      className="glass-card border-2 border-accent/30 overflow-hidden"
      data-testid="prayer-widget"
    >
      {/* Header with Live Clock */}
      <div className="bg-primary px-6 py-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl text-primary-foreground flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t('prayers.title')}
          </h3>
          <span className="text-xs text-primary-foreground/70 bg-primary-foreground/10 px-2 py-1 rounded">
            {t('prayers.timezone')}
          </span>
        </div>
        {/* Live Clock Display */}
        <div className="mt-3 text-center">
          <p className="text-3xl font-heading text-primary-foreground font-bold ltr-text" data-testid="live-clock">
            {formatCurrentTime()}
          </p>
          {/* Gregorian Date */}
          <p className="text-sm text-primary-foreground/80 mt-1">
            {formatGregorianDate()}
          </p>
          {/* Hijri Date */}
          <p className="text-sm text-accent font-semibold mt-1 ltr-text" data-testid="hijri-date">
            🌙 {formatHijriDate()}
          </p>
        </div>
      </div>

      <CardContent className="p-0">
        {/* Prayer Times Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-0">
          {prayers.map((prayer, index) => (
            <div
              key={index}
              className="px-4 py-4 flex flex-col items-center justify-center border-b border-r border-border last:border-r-0 hover:bg-accent/5 transition-colors"
              data-testid={`prayer-${prayer.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${prayer.color} text-white mb-2`}>
                <prayer.icon className="h-5 w-5" />
              </div>
              <span className="font-medium text-foreground text-sm">{prayer.name}</span>
              <span className="font-heading text-lg font-semibold text-primary ltr-text mt-1">
                {prayer.time}
              </span>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <div className="px-6 py-3 bg-muted/50 text-center">
          <p className="text-xs text-muted-foreground">
            {t('prayers.note')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PrayerWidget;
